import React, { useContext, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Button,
  Avatar,
  useTheme,
  Dialog,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import { ChatContext } from "../../context/ChatContext";
import { AuthContext } from "../../context/AuthContext";
import LeftPanel from "../LeftPanel";
import { clearUserHistory, clearSessionFiles } from "../../utils/storage";

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 80;

const Sidebar = ({
  history,
  onFileParsed,
  onClearHistory,
  onOpenHistory,
  setSidebarWidth,
  activeConvId,
  chatRef
}) => {
  const { loadChat } = useContext(ChatContext);
  const { user } = useContext(AuthContext);
  const [collapsed, setCollapsed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false); // ✅ dialog state
  const theme = useTheme();

  const username =
    user?.email || user?.username || user?.firstName || "Username";

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const newWidth = prev ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
      setSidebarWidth?.(newWidth);
      return !prev;
    });
  };

  const bgGradient =
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(30,41,59,0.95), rgba(17,24,39,0.95))"
      : "linear-gradient(180deg, rgba(240,244,248,0.95), rgba(226,232,240,0.95))";

  const iconColor = theme.palette.mode === "dark" ? "#94a3b8" : "#475569";

  // ✅ Handler for clearing history with dialog
  const handleClearHistory = () => {
    setConfirmOpen(true);
  };

  // const confirmDelete = () => {
  //   onClearHistory?.();
  //   setConfirmOpen(false);
  // };
  const confirmDelete = () => {
    if (user?.userId) {
      clearUserHistory(user.userId); // remove history for this user
      clearSessionFiles(user.userId); // remove session files for this user
    }
    onClearHistory?.(); // optional callback to update React state
    setConfirmOpen(false);
  };

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
            transition: "width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            boxSizing: "border-box",
            height: "100%",
            overflowX: "hidden",
            background: bgGradient,
            backdropFilter: "blur(16px)",
            borderRight: "1px solid rgba(0,0,0,0.05)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header with Username & Toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            px: collapsed ? 0 : 1.5,
            py: 1.2,
          }}
        >
          {!collapsed && (
            <Box
              sx={{
                px: 2,
                py: 1,
                ml: 2,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(90deg,#4338ca,#2563eb)"
                    : "linear-gradient(90deg,#38bdf8,#9333ea)",
                color: "#fff",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  width: 20,
                  height: 20,
                  fontSize: "0.9rem",
                  mr: 1,
                  fontWeight: 700,
                }}
              >
                {username[0].toUpperCase()}
              </Avatar>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                fontSize="0.8rem"
              >
                {username}
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={toggleCollapse}
            size="large"
            sx={{
              color: iconColor,
              ml: collapsed ? 0 : 1,
              transition: "transform 0.3s ease-in-out",
              "&:hover": { transform: "scale(1.15)" },
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* Navigation */}
        <List sx={{ pt: 1 }}>
          <Tooltip
            title="History"
            placement="right"
            disableHoverListener={!collapsed}
          >
            <ListItem
              button
              sx={{
                justifyContent: collapsed ? "center" : "flex-start",
                px: 0,
                "&:hover": { background: "transparent" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "70%",
                  borderRadius: "12px",
                  mx: collapsed ? 0 : 2,
                  px: 2,
                  py: 1.2,
                  transition: "all 0.3s ease",
                  "&:hover": { bgcolor: "rgba(11, 116, 255, 0.15)" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 48,
                    color: "#0b74ff",
                    justifyContent: "flex-start",
                  }}
                >
                  <HistoryIcon />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          background: "linear-gradient(90deg,#2563eb,#9333ea)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        History
                      </Typography>
                    }
                  />
                )}
              </Box>
            </ListItem>
          </Tooltip>
        </List>

        {/* Clear History in Collapsed Mode */}
        {collapsed && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            <Tooltip title="Clear History" placement="right">
              <Button
                onClick={handleClearHistory} // ✅ open dialog
                variant="text"
                sx={{
                  minWidth: "auto",
                  p: 1,
                  color: theme.palette.mode === "dark" ? "#ef4444" : "#b91c1c",
                }}
              >
                <DeleteIcon sx={{ fontSize: 24 }} />
              </Button>
            </Tooltip>
          </Box>
        )}

        {/* Left Panel */}
        {!collapsed && (
          <Box sx={{ flexGrow: 1, px: 2, overflow: "hidden" }}>
            <LeftPanel
              onFileParsed={onFileParsed}
              history={history}
              onClearHistory={handleClearHistory} // ✅ use dialog
              onOpenHistory={onOpenHistory}
              darkMode={theme.palette.mode === "dark"}
              activeConvId={activeConvId}
              chatRef={chatRef}
            />
          </Box>
        )}
      </Drawer>

      {/* ✅ Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Delete All Chats
          </Typography>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to delete all chats? This action cannot be
            undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              sx={{ mr: 1 }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default Sidebar;
